import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import {
  FarmIssueRule,
  FarmOverviewRow,
  FarmStatus,
  farmStatusOf,
  isoWeekSpan,
  parseFarmIssueRule
} from '../../models/farms-overview';
import { buildFarmDashboardPath } from '../../models/dashboard';
import { useAnchorRect } from '../../hooks/useAnchorRect';
import { HoverCard } from '../../elements/ui/hover-card';
import { pigletRegistrationColors as c } from '../PigletRegistration/colors';
import FarmTrendSparkline from './FarmTrendSparkline';
import FarmBirthWeightBadge from './FarmBirthWeightBadge';
import FarmSyncBadge from './FarmSyncBadge';
import FarmAntibioticsBadge from './FarmAntibioticsBadge';

const num = (value: number) => value.toLocaleString();
const pct = (value: number) => `${Math.round(value)}%`;

/**
 * What the farm was scored over, as a phrase: "the last 2 completed cycles (wk 37–40)". The status, the
 * row detail and the chip's hover all quote it, because a fixed number of weeks is not what was scored: the
 * window is whole completed cycles, so a 4-week farm is scored on one cycle and a 3-week farm on two.
 */
const useScoredScope = (farm: FarmOverviewRow): string => {
  const { t } = useTranslation();

  if (farm.scoredCycles <= 0 || !farm.scoredFrom || !farm.scoredTo) return t('f-farms-overview-scope-none');

  const span = isoWeekSpan(farm.scoredFrom, farm.scoredTo);
  const range =
    span.first === span.last
      ? t('f-farms-overview-week-single', { week: span.first })
      : t('f-farms-overview-week-range', { from: span.first, to: span.last });

  return t('f-farms-overview-scope', { count: farm.scoredCycles, range });
};

/**
 * Dot colour and label per status. Red and amber are the two the queue already used; green is the one this
 * screen previously had no way to say, and grey is deliberately not green - a farm nobody configured is
 * unknown, not fine.
 */
const STATUS_CHIP: Record<FarmStatus, { color: string; labelKey: string }> = {
  [FarmStatus.Critical]: { color: c.belowExpected, labelKey: 'f-farms-overview-severity-critical' },
  [FarmStatus.Warning]: { color: c.target, labelKey: 'f-farms-overview-severity-warning' },
  [FarmStatus.OnTrack]: { color: c.neutral, labelKey: 'f-farms-overview-status-on-track' },
  [FarmStatus.NotMonitored]: { color: c.expected, labelKey: 'f-farms-overview-status-not-monitored' }
};

/** The sentence the status chip's hover card states: what was scored, over which cycles, and the result. */
const useStatusExplanation = (farm: FarmOverviewRow, status: FarmStatus, scope: string): string => {
  const { t } = useTranslation();
  const label = t(STATUS_CHIP[status].labelKey);

  if (status === FarmStatus.NotMonitored) return t('f-farms-overview-status-hover-not-monitored');

  if (farm.scoredCycles <= 0) return t('f-farms-overview-status-hover-no-cycle', { status: label });

  if (parseFarmIssueRule(farm.rule) === FarmIssueRule.NoRegistrations) {
    return t('f-farms-overview-status-hover-none', {
      status: label,
      count: farm.consecutiveCyclesWithoutRegistrations
    });
  }

  const sentence = t('f-farms-overview-status-hover', {
    status: label,
    registered: num(farm.registered),
    expected: num(farm.expected),
    scope,
    percent: pct(farm.percentOfExpected)
  });

  // The likely causes of an over-registration are not in the numbers, and the row no longer has a line of
  // its own to say them, so they ride along here.
  return parseFarmIssueRule(farm.rule) === FarmIssueRule.OverRegistered
    ? `${sentence} ${t('f-farms-overview-issue-over-detail')}`
    : sentence;
};

/**
 * The status, with the reason for it on hover. Focus opens the card as well, like the birth weight badge
 * beside it: the scored cycles are named nowhere else, so reading them cannot depend on having a pointer.
 */
const StatusChip = ({ status, explanation }: { status: FarmStatus; explanation: string }) => {
  const { t } = useTranslation();
  const chip = STATUS_CHIP[status];
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const { anchor, openByPointer, openByFocus, clear } = useAnchorRect(wrapperRef);

  return (
    <span
      ref={wrapperRef}
      tabIndex={0}
      onMouseEnter={openByPointer}
      onMouseLeave={clear}
      onFocus={openByFocus}
      onBlur={clear}
      className="inline-flex shrink-0 cursor-help items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: chip.color }} />
      {t(chip.labelKey)}

      {anchor && (
        <HoverCard anchor={anchor} className="max-w-80">
          <p className="text-gray-700">{explanation}</p>
        </HoverCard>
      )}
    </span>
  );
};

export interface FarmQueueRowProps {
  farm: FarmOverviewRow;
  /** Weeks the antibiotics count and the birth weight figures cover, quoted in their hover text. */
  lookbackWeeks: number;
  /** The share of registrations a farm may leave unweighed, as the API reports it; null when it did not. */
  birthWeightTolerancePercent: number | null;
}

/**
 * One farm, flagged or not. Every row is actionable: the only control is the one that opens that farm's
 * dashboard, so the admin lands on the numbers behind the badge rather than on another summary.
 */
export default function FarmQueueRow({ farm, lookbackWeeks, birthWeightTolerancePercent }: FarmQueueRowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scope = useScoredScope(farm);
  const status = farmStatusOf(farm);
  const explanation = useStatusExplanation(farm, status, scope);

  // farmName is nullable in the response; the UBN always exists and is what an admin searches by anyway.
  const displayName = farm.farmName || farm.farmNumber;

  const openFarm = () =>
    // The name and number ride along so the dashboard can title itself immediately instead of showing an
    // id while it waits for a farm lookup it would otherwise have to make.
    navigate(buildFarmDashboardPath(farm.farmId), {
      state: { farmName: displayName, farmNumber: farm.farmNumber }
    });

  return (
    <div className="grid grid-cols-1 items-center gap-4 rounded-lg border border-transparent px-3 py-3 transition-colors hover:border-gray-200 hover:bg-gray-50 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)_auto_auto]">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-gray-900">{displayName}</p>
        <p className="mt-0.5 truncate text-xs text-gray-500">
          {farm.hasProductionSchedule
            ? t('f-farms-overview-farm-meta-cycle', {
                number: farm.farmNumber,
                expected: num(farm.expectedPerCycle),
                weeks: farm.cycleWeeks
              })
            : t('f-farms-overview-farm-meta-weekly', {
                number: farm.farmNumber,
                expected: num(farm.expectedPerCycle)
              })}
        </p>
      </div>

      <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
        <StatusChip status={status} explanation={explanation} />
        {/* Second badge, its own question: the status says whether the farm registers what it should,
            this says whether it weighs what it registers. */}
        <FarmBirthWeightBadge
          farm={farm}
          lookbackWeeks={lookbackWeeks}
          tolerancePercent={birthWeightTolerancePercent}
        />
        <FarmAntibioticsBadge farm={farm} lookbackWeeks={lookbackWeeks} />
        <FarmSyncBadge farm={farm} />
      </div>

      <FarmTrendSparkline cycles={farm.cycles} farmName={displayName} />

      <button
        type="button"
        onClick={openFarm}
        className="inline-flex h-9 cursor-pointer appearance-none items-center gap-1.5 justify-self-start rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 lg:justify-self-end"
      >
        {t('f-farms-overview-open-farm')}
        <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
      </button>
    </div>
  );
}
